import React, { useEffect, useMemo, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Zahlungslauf-Wizard
 * - Lädt PostFinance-Connections
 * - Aggregiert ausgewählte Bills (mit Lieferanten-IBAN, editierbar)
 * - Submit → POST /companies/{cid}/postfinance/payment-batches
 */
export function PaymentRunWizard({ open, onClose, companyId, selectedBills, suppliers, onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);
  const [connections, setConnections] = useState([]);
  const [connectionId, setConnectionId] = useState("");
  const [executionDate, setExecutionDate] = useState(today);
  const [debtorName, setDebtorName] = useState("");
  const [debtorIban, setDebtorIban] = useState("");
  const [lines, setLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingConn, setLoadingConn] = useState(false);

  // Initialisiere Lines aus selectedBills + Suppliers
  useEffect(() => {
    if (!open) return;
    const items = (selectedBills || []).map((b) => {
      const sup = suppliers.find((s) => s._id === b.supplier_id);
      return {
        bill_id: b._id,
        bill_number: b.bill_number,
        amount: parseFloat(b.total) || 0,
        currency: b.currency || "CHF",
        creditor_name: sup?.name || "",
        creditor_iban: (sup?.iban || "").replace(/\s+/g, "").toUpperCase(),
        remittance_info: `Rechnung ${b.bill_number}`,
      };
    });
    setLines(items);
    setExecutionDate(today);
  }, [open, selectedBills, suppliers]);

  // Lade Connections + Firmen-IBAN
  useEffect(() => {
    if (!open || !companyId) return;
    let cancel = false;
    (async () => {
      setLoadingConn(true);
      try {
        const [connRes, compRes] = await Promise.all([
          apiClient.get(`/companies/${companyId}/postfinance/connections`),
          apiClient.get(`/my/companies/${companyId}`).catch(() => ({ data: null })),
        ]);
        if (cancel) return;
        const ready = (connRes.data || []).filter((c) => c.status === "ready");
        setConnections(ready);
        if (ready.length > 0) setConnectionId(ready[0].id);

        if (compRes.data) {
          setDebtorName(compRes.data.name || "");
          setDebtorIban((compRes.data.iban || "").replace(/\s+/g, "").toUpperCase());
        }
      } catch (e) {
        console.error(e);
        toast.error("Fehler beim Laden der PostFinance-Verbindungen");
      } finally {
        setLoadingConn(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open, companyId]);

  // Sync Connection → Auto-fill Debtor-IBAN
  useEffect(() => {
    if (!connectionId) return;
    const conn = connections.find((c) => c.id === connectionId);
    if (conn?.iban) setDebtorIban(conn.iban.replace(/\s+/g, "").toUpperCase());
  }, [connectionId, connections]);

  const totals = useMemo(() => {
    const sum = lines.reduce((acc, l) => acc + (parseFloat(l.amount) || 0), 0);
    return { total: Math.round(sum * 100) / 100, count: lines.length };
  }, [lines]);

  const validIban = (s) => {
    if (!s) return false;
    const v = s.replace(/\s+/g, "").toUpperCase();
    return /^CH[0-9]{2}[A-Z0-9]{17}$|^LI[0-9]{2}[A-Z0-9]{17}$/.test(v);
  };

  const invalidLines = lines.filter((l) => !validIban(l.creditor_iban) || !l.creditor_name);
  const canSubmit =
    connectionId &&
    executionDate &&
    debtorName &&
    validIban(debtorIban) &&
    lines.length > 0 &&
    invalidLines.length === 0 &&
    !submitting;

  const updateLine = (idx, key, value) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [key]: value } : l)));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        connection_id: connectionId,
        debtor_name: debtorName,
        debtor_iban: debtorIban.replace(/\s+/g, "").toUpperCase(),
        requested_execution_date: executionDate,
        upload_now: true,
        payments: lines.map((l) => ({
          bill_id: l.bill_id,
          creditor_name: l.creditor_name,
          creditor_iban: l.creditor_iban.replace(/\s+/g, "").toUpperCase(),
          amount: parseFloat(l.amount) || 0,
          currency: l.currency || "CHF",
          remittance_info: l.remittance_info || null,
        })),
      };
      const res = await apiClient.post(
        `/companies/${companyId}/postfinance/payment-batches`,
        payload,
      );
      const data = res.data;
      if (data.upload_status === "uploaded") {
        toast.success(
          `Zahlungslauf erstellt: ${data.payment_count} Zahlungen · ${data.total_amount.toFixed(2)} ${data.currency} · pain.001 hochgeladen`,
        );
      } else if (data.upload_status === "upload_failed") {
        toast.warning(
          `pain.001 erstellt, Upload fehlgeschlagen: ${data.upload_error || "Unbekannt"}. XML kann manuell heruntergeladen werden.`,
        );
      } else {
        toast.info(`pain.001 erstellt (Status: ${data.upload_status})`);
      }
      onSuccess?.(data);
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Fehler beim Erstellen des Zahlungslaufs");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="payment-run-wizard-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-emerald-600" />
            Zahlungslauf erstellen
          </DialogTitle>
          <DialogDescription>
            Erzeugt eine ISO-20022 pain.001-Datei mit allen ausgewählten
            Eingangsrechnungen und lädt sie nach PostFinance hoch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Konfiguration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>PostFinance-Verbindung</Label>
              <Select value={connectionId} onValueChange={setConnectionId}>
                <SelectTrigger data-testid="payment-run-connection-select">
                  <SelectValue placeholder={loadingConn ? "Lädt …" : "Verbindung wählen"} />
                </SelectTrigger>
                <SelectContent>
                  {connections.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} · {c.environment === "test" ? "Test" : "Live"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!loadingConn && connections.length === 0 && (
                <p className="text-xs text-rose-700 mt-1">
                  Keine bereit konfigurierten PostFinance-Verbindungen. Bitte zuerst in
                  Einstellungen → PostFinance einrichten.
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Ausführungsdatum</Label>
              <SwissDateField
                
                value={executionDate}
                onChange={(e) => setExecutionDate(e.target.value)}
                data-testid="payment-run-execution-date"
              />
            </div>
            <div className="space-y-1">
              <Label>Auftraggeber-Name</Label>
              <Input
                value={debtorName}
                onChange={(e) => setDebtorName(e.target.value)}
                placeholder="Name Ihrer Firma"
                data-testid="payment-run-debtor-name"
              />
            </div>
            <div className="space-y-1">
              <Label>
                Auftraggeber-IBAN
                {!validIban(debtorIban) && debtorIban && (
                  <span className="text-rose-600 ml-1">· ungültig</span>
                )}
              </Label>
              <Input
                value={debtorIban}
                onChange={(e) => setDebtorIban(e.target.value)}
                placeholder="CH00 0000 0000 0000 0000 0"
                data-testid="payment-run-debtor-iban"
              />
            </div>
          </div>

          {/* Bills-Tabelle */}
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <Table data-testid="payment-run-lines-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Rechnung</TableHead>
                  <TableHead>Empfänger</TableHead>
                  <TableHead>IBAN</TableHead>
                  <TableHead className="text-right w-28">Betrag</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l, i) => {
                  const ibanOk = validIban(l.creditor_iban);
                  return (
                    <TableRow key={l.bill_id} data-testid={`payment-run-line-${l.bill_id}`}>
                      <TableCell className="text-xs font-mono text-slate-700">
                        {l.bill_number}
                      </TableCell>
                      <TableCell>
                        <Input
                          value={l.creditor_name}
                          onChange={(e) => updateLine(i, "creditor_name", e.target.value)}
                          className="h-8 text-xs"
                          data-testid={`payment-run-line-name-${l.bill_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={l.creditor_iban}
                          onChange={(e) => updateLine(i, "creditor_iban", e.target.value)}
                          className={`h-8 text-xs font-mono ${
                            ibanOk ? "" : "border-rose-400 bg-rose-50"
                          }`}
                          placeholder="CH..."
                          data-testid={`payment-run-line-iban-${l.bill_id}`}
                        />
                      </TableCell>
                      <TableCell className="text-right text-xs font-medium">
                        {(parseFloat(l.amount) || 0).toFixed(2)} {l.currency}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Validation-Banner */}
          {invalidLines.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <strong>{invalidLines.length}</strong> Zeile(n) haben ungültige oder fehlende IBAN /
                Empfänger-Namen. Bitte korrigieren — Tipp: IBAN beim Lieferanten in den Stammdaten
                hinterlegen, dann wird sie hier automatisch übernommen.
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="text-sm text-emerald-900">
              <span className="font-semibold">{totals.count}</span> Zahlung(en) ·{" "}
              <span className="font-semibold">{totals.total.toFixed(2)} CHF</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              ISO-20022 pain.001
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
              Abbrechen
            </Button>
            <Button
              type="button"
              size="sm"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5"
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="payment-run-submit-button"
            >
              <Send className="h-4 w-4 mr-1" />
              {submitting ? "Erstelle pain.001 …" : "Zahlungslauf erstellen & hochladen"}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Hinweis: Die Datei wird nach <code>pain-001-in/</code> hochgeladen. Sie müssen den
            Auftrag in PostFinance E-Finance final freigeben.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
